import React, { useState, useEffect, useMemo } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import UserController from './permissions/controller/user-controller';
import AddUser from './permissions/components/AddUser';
import Modal from '../../components/Modal';
const PermissionsPage = () => {
  // State for the users page
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageSizeOptions] = useState([5, 10, 20, 50]);
  const [totalItems, setTotalItems] = useState(0);
  const [maxPage, setMaxPage] = useState(1);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isViewUserModalOpen, setIsViewUserModalOpen] = useState(false);
  const [viewedUser, setViewedUser] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });


  // Fetch users from API
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await UserController.fetchUsers(currentPage, pageSize, '', 'Utilisateur');
        setUsers(response.list || []);
        setTotalItems(response.total || 0);
        setMaxPage(response.maxPage || 1);
        setError(null);
      } catch (error) {
        console.error('Error fetching users:', error);
        setError('Une erreur est survenue lors du chargement des utilisateurs.');
        toast.error('Erreur de chargement des utilisateurs');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [currentPage, pageSize]);

  // Handle search change without debounce since we're filtering locally
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

 // Add this effect to filter users based on searchTerm
  useEffect(() => {
    let filtered = [...users];

    if (!searchTerm.trim()) {
      // If no search term, show all users
      setFilteredUsers(users);
    }


    if(searchTerm){
    const term = searchTerm.toLowerCase();

    // Filter users based on name, email, and phone
    filtered = users.filter(user =>
      user.firstName?.toLowerCase().includes(term) ||
      user.lastName?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term) ||
      user.phoneNumber?.toLowerCase().includes(term)
    );


    }
            // Apply status filter
  if (statusFilter !== 'all') {
  filtered = filtered.filter(user => user.active.toString() === statusFilter);
            }


    setFilteredUsers(filtered);
  }, [searchTerm, users,statusFilter]);

  // Handle page size change
  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Helper function for rendering page numbers with ellipsis
  const renderPageNumbers = () => {
    if (maxPage <= 1) {
      return null;
    }

    // Function to render a single page button
    const renderPageButton = (pageNum) => (
      <button
        key={pageNum}
        onClick={() => setCurrentPage(pageNum)}
        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium ${
          currentPage === pageNum
            ? 'z-10 bg-[#174540]/30 border-[#174540] text-[#174540]'
            : 'text-gray-700 hover:bg-gray-50'
        }`}
      >
        {pageNum}
      </button>
    );

    // For small number of pages, show all
    if (maxPage <= 7) {
      return [...Array(maxPage)].map((_, i) => renderPageButton(i + 1));
    }

    // For many pages, show with ellipsis
    const pages = [];

    // Always show first page
    pages.push(renderPageButton(1));

    // Show ellipsis if needed
    if (currentPage > 3) {
      pages.push(
        <span key="ellipsis-1" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
          ...
        </span>
      );
    }

    // Show current page and neighbors
    const startNeighbor = Math.max(2, currentPage - 1);
    const endNeighbor = Math.min(maxPage - 1, currentPage + 1);

    for (let i = startNeighbor; i <= endNeighbor; i++) {
      pages.push(renderPageButton(i));
    }

    // Show ellipsis if needed
    if (currentPage < maxPage - 2) {
      pages.push(
        <span key="ellipsis-2" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
          ...
        </span>
      );
    }

    // Always show last page
    pages.push(renderPageButton(maxPage));

    return pages;
  };

  const handleCreateUser = () => {
    setIsAddUserModalOpen(true);
  };


  function jsonToFormData(json) {
    const formData = new FormData();

    for (const key in json) {
      if (json.hasOwnProperty(key)) {
        const value = json[key];
        if(!value&&typeof value !== 'boolean') continue
     //   formData.append(key, value);
     const formKey=key.charAt(0).toUpperCase() + key.slice(1)

        // Handle arrays
        if (Array.isArray(value)) {
          value.forEach((item, index) => {
            formData.append(`${formKey}[${index}]`, item);
                    });
        }
        // Handle nested objects (optional: flattening or skipping)
        // else if (typeof value === 'object' && value !== null) {
        //   // If you want to stringify nested objects:
        //   formData.append(key, JSON.stringify(value));
        // }
        // Primitive values
        else {

          formData.append(formKey, value);
        }
      }
    }

    return formData;
  }

  const handleSubmitNewUser = async (userData) => {
    setIsSubmitting(true);


    try {
      const formData=jsonToFormData(userData)
      await UserController.createUser(formData);
      toast.success('Utilisateur ajouté avec succès');
      setIsAddUserModalOpen(false);

      // Refresh the user list
      const response = await UserController.fetchUsers(currentPage, pageSize, searchTerm, 'Utilisateur');
      setUsers(response.list || []);
      setTotalItems(response.total || 0);
      setMaxPage(response.maxPage || 1);
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.errorMessage||'Erreur lors de l\'ajout du utilisateur');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditUser = async (user) => {

    const detailedUser = await UserController.getUserById(user.id);

    setCurrentUser(detailedUser);
    setIsEditUserModalOpen(true);
  };

  const handleSubmitEditUser = async (userData) => {
    setIsSubmitting(true);
    try {
      // Merge the ID from currentUser with the updated data
      const updatedUser = {
        ...userData,
        id: currentUser.id
      };


      if (updatedUser.image && !(updatedUser.image instanceof File)) {
        delete updatedUser.image
}
if (updatedUser.image && (updatedUser.image instanceof File)) {
   updatedUser.ImageUpdated = true
}else{
  updatedUser.ImageUpdated = false

}


      const formData=jsonToFormData(updatedUser)

      await UserController.updateUser(formData);
      toast.success('Utilisateur mis à jour avec succès');
      setIsEditUserModalOpen(false);

      // Refresh the user list
      const response = await UserController.fetchUsers(currentPage, pageSize, searchTerm, 'Utilisateur');
      setUsers(response.list || []);
      setTotalItems(response.total || 0);
      setMaxPage(response.maxPage || 1);
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Erreur lors de la mise à jour de l\'utilusateur');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    setIsSubmitting(true);
    try {
      await UserController.deleteUser(userToDelete.id);
      toast.success('Utilisateur supprimé avec succès');

      // Refresh the user list
      const response = await UserController.fetchUsers(currentPage, pageSize, searchTerm, 'Utilisateur');

      // Check if we need to go back a page (if we deleted the last item on the current page)
      if (response.list.length === 0 && currentPage > 1) {
        setCurrentPage(prev => prev - 1);
      } else {
        setUsers(response.list || []);
        setTotalItems(response.total || 0);
        setMaxPage(response.maxPage || 1);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Erreur lors de la suppression du utilisateur');
    } finally {
      setIsSubmitting(false);
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
    }
  };

  const handleViewUser = async (user) => {
    try {
      setLoading(true);
      // Fetch the complete user details by ID
      const detailedUser = await UserController.getUserById(user.id);
      setViewedUser(detailedUser);
      setIsViewUserModalOpen(true);
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast.error('Erreur lors du chargement des détails du utilisateur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Utilisateurs</h1>
          <p className="mt-2 text-sm text-gray-700">
            Liste des Utilisateurs de votre boutique.
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            type="button"
            onClick={handleCreateUser}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Nouveau Utilusateur
          </button>
        </div>
      </div>






      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
      <div className="sm:col-span-2">

        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
            placeholder="Rechercher un utilisateur par nom, email ou telephone..."
            value={searchTerm}
            onChange={handleSearchChange}
          />

          {searchTerm && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button
                onClick={() => setSearchTerm('')}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

        </div>
        </div>
        <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm rounded-md"
          >
            <option value="all">All </option>
            <option value={'true'}>Active</option>
            <option value={'false'}>Inactive</option>
          </select>


      </div>

      <div className="mt-8">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#174540]"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-red-500 mb-2">{error}</div>
            <button
              onClick={() => window.location.reload()}
              className="text-[#174540] hover:underline"
            >
              Réessayer
            </button>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-12 bg-white shadow overflow-hidden rounded-lg">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                vectorEffect="non-scaling-stroke"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun utilisateur trouvé</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? "Essayez d'autres termes de recherche." : "Commencez par créer un nouveau utilisateur."}
            </p>
          </div>
        ) : (
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            {/* Desktop view */}
            <div className="hidden md:block min-w-full">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Utilisateur
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Téléphone
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">

                      <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={user.imagePath||'https://randomuser.me/api/portraits/men/40.jpg'}
                            alt={user.firstName}
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.firstName} {user.lastName}</div>
                        </div>
                      </div>
                    </td>

                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{user.phoneNumber}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-3">
                          <button
                            className="text-gray-600 hover:text-gray-900"
                            title="Voir les détails"
                            onClick={() => handleViewUser(user)}
                          >
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          <button
                            className="text-gray-600 hover:text-gray-900"
                            title="Éditer le utilisateur"
                            onClick={() => handleEditUser(user)}
                          >
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900"
                            title="Supprimer le utilisateur"
                            onClick={() => handleDeleteClick(user)}
                          >
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile view */}
            <div className="md:hidden">
              <ul className="divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <li key={user.id} className="px-4 py-4">
                    <div className="space-y-3">
                      <div className="flex justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={user.imagePath||'https://randomuser.me/api/portraits/men/40.jpg'}
                            alt={user.firstName}
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.firstName} {user.lastName}</div>
                        </div>
                      </div>
                        <div className="flex space-x-2">
                          <button
                            className="text-gray-600 hover:text-gray-900 p-1"
                            title="Voir les détails"
                            onClick={() => handleViewUser(user)}
                          >
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          <button
                            className="text-gray-600 hover:text-gray-900 p-1"
                            title="Éditer le utilisateur"
                            onClick={() => handleEditUser(user)}
                          >
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Supprimer le utilisateur"
                            onClick={() => handleDeleteClick(user)}
                          >
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-1">
                        <div className="flex items-center text-sm">
                          <svg className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {user.phoneNumber}
                        </div>
                        <div className="flex items-center text-sm">
                          <svg className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {user.email}
                        </div>
                        <div className="flex items-center text-sm">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.active ? 'Active' : 'Inactive'}
                      </span>

                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredUsers.length > 0 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6 rounded-lg shadow">
          <div className="flex items-center">
            <span className="text-sm text-gray-700 mr-4">
              Affichage de {Math.min(filteredUsers.length, (currentPage - 1) * pageSize + 1)} à {Math.min(currentPage * pageSize, filteredUsers.length)} sur {filteredUsers.length} utilisateurs
            </span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              className="h-full rounded-md border border-gray-300 bg-white py-0 pl-2 pr-7 text-gray-500 focus:ring-2 focus:ring-inset focus:ring-[#174540] sm:text-sm"
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            {/* Previous Button */}
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                currentPage === 1
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-500 hover:bg-gray-50 cursor-pointer'
              }`}
            >
              <span className="sr-only">Précédent</span>
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>

            {/* Page Numbers */}
            {renderPageNumbers()}

            {/* Next Button */}
            <button
              onClick={() => setCurrentPage(prev => Math.min(maxPage, prev + 1))}
              disabled={currentPage === maxPage}
              className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                currentPage === maxPage
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-500 hover:bg-gray-50 cursor-pointer'
              }`}
            >
              <span className="sr-only">Suivant</span>
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </nav>
        </div>
      )}

      <Modal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        title="Ajouter un nouveau utilisateur"
        onSubmit={() => document.getElementById('addUserForm').requestSubmit()}
        size="6xl"
        isSubmitting={isSubmitting}
        submitLabel="Créer utilisateur"
      >
        <AddUser
          formId="addUserForm"
          onSubmit={handleSubmitNewUser}
          isSubmitting={isSubmitting}
        />
      </Modal>

      <Modal
        isOpen={isEditUserModalOpen}
        onClose={() => setIsEditUserModalOpen(false)}
        title="Modifier le utilisateur"
        onSubmit={() => document.getElementById('editUserForm').requestSubmit()}
        size="6xl"
        isSubmitting={isSubmitting}
        submitLabel="Enregistrer les modifications"
      >
        {currentUser && (
          <AddUser
            formId="editUserForm"
           onSubmit={handleSubmitEditUser}
            isSubmitting={isSubmitting}
            initialData={currentUser}
          />
        )}
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmer la suppression"
        onSubmit={handleConfirmDelete}
        size="md"
        isSubmitting={isSubmitting}
        submitLabel="Supprimer"
        submitColor="red"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-center text-red-500">
            <svg className="h-12 w-12" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </div>

          <p className="text-center text-gray-700">
            Êtes-vous sûr de vouloir supprimer le utilisateur <span className="font-semibold text-gray-900">{userToDelete?.name}</span> ?
          </p>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-6">
            <div className="flex">
              <svg className="h-6 w-6 text-yellow-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Cette action est irréversible. Toutes les données associées à ce utilisateur seront définitivement supprimées.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isViewUserModalOpen}
        onClose={() => setIsViewUserModalOpen(false)}
        title="Détails du utilisateur"
        size="lg"
        hideSubmitButton={true}
      >
        {viewedUser ? (
          <div className="space-y-8">
            {/* Header with name and actions */}
            <div className="flex items-center justify-between border-b border-gray-200 pb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{viewedUser.firstName} {viewedUser.lastName}</h2>
                <p className="text-sm text-gray-500">USERNAME: {viewedUser.userName}</p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setIsViewUserModalOpen(false);
                    handleEditUser(viewedUser);
                  }}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
                >
                  <svg className="-ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  Modifier
                </button>
                <button
                  onClick={() => {
                    setIsViewUserModalOpen(false);
                    handleDeleteClick(viewedUser);
                  }}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-red-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <svg className="-ml-1 mr-2 h-4 w-4 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Supprimer
                </button>
              </div>
            </div>

            {/* Contact information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <svg className="h-5 w-5 mr-2 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
                Coordonnées de utilisateur
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#174540]/10 flex items-center justify-center">
                      <svg className="h-5 w-5 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-xs font-medium text-gray-500 uppercase">Téléphone</p>
                      <p className="text-sm font-semibold text-gray-900">{viewedUser.phoneNumber}</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#174540]/10 flex items-center justify-center">
                      <svg className="h-5 w-5 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-xs font-medium text-gray-500 uppercase">Email</p>
                      <p className="text-sm font-semibold text-gray-900">{viewedUser.email}</p>
                    </div>
                  </div>
                </div>

                <div>

                </div>
              </div>
            </div>

            {/* Additional information section - can be expanded with more data as needed */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <svg className="h-5 w-5 mr-2 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Statistiques et informations
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase">Date d'ajout</p>
                  <p className="text-sm font-semibold text-gray-900">{new Date().toLocaleDateString()}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase">Statut</p>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {viewedUser.active? "Active":'Inactive'}
                  </span>
                </div>

                {/* <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase">Commandes</p>
                  <p className="text-sm font-semibold text-gray-900">0 commandes</p>
                </div> */}
              </div>
            </div>

            {/* Footer with actions */}

          </div>
        ) : (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#174540]"></div>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default PermissionsPage;
